package com.eladfinish.bible.contest.screens.browse.usecases

import com.eladfinish.bible.contest.common.usecases.RemoveGodNamesUseCase
import com.eladfinish.bible.contest.common.usecases.RemoveNikudAndMakafUseCase
import com.eladfinish.bible.contest.common.usecases.RemoveTeamimUseCase
import com.eladfinish.bible.contest.constants.BooleanConstants
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.constants.SharedPrefsConstants
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.KEY_EACH_VERSE_IN_NEW_LINE
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.KEY_REMOVE_VERSE_LETTER
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.utils.MyStringUtils.replaceSpaceWithNoBreakSpace
import com.eladfinish.bible.contest.utils.getRegexOfSingleOtPasukAleph
import com.eladfinish.bible.contest.utils.removeWhitespacesAndParshiot
import com.eladfinish.bible.contest.utils.replaceKtivWithQriNew
import com.eladfinish.bible.contest.utils.replaceOtiotPasuk
import com.russhwolf.settings.Settings

class PrepareChapterContentForDisplayUseCase(
    private val settings: Settings,
    private val removeTeamimUseCase: RemoveTeamimUseCase,
    private val removeNikudAndMakafUseCase: RemoveNikudAndMakafUseCase,
    private val removeGodNamesUseCase: RemoveGodNamesUseCase,
) {

    /**
     * IMPORTANT: Execution order matters. Keep the pipeline order stable unless you fully validate changes.
     */
    operator fun invoke(
        rawText: String,
        isTikunKorim: Boolean = false,
        forceOnlyQri: Boolean = false,
        forceRemoveTeamim: Boolean = false,
        forceRemoveNikud: Boolean = false,
        forceHideGodNames: Boolean = false,
    ): String {
        return rawText
            // THE EXECUTION ORDER IS IMPORTANT!
            .replaceKtivWithQriIfNeeded(force = forceOnlyQri, isTikunKorim = isTikunKorim)
            .removeWhitespacesAndParshiot(isTikunKorim = isTikunKorim)
            .replaceSpaceWithNoBreakSpace()
            .removeTeamimIfNeeded(force = forceRemoveTeamim)
            .removeNikudIfNeeded(force = forceRemoveNikud)
            .applyEachVerseInNewLineIfNeeded()
            .removeVerseLetterIfNeeded(isTikunKorim = isTikunKorim)
            .removeGodNamesIfNeeded(force = forceHideGodNames)
    }

    private inline fun String.runIf(condition: Boolean, block: String.() -> String): String =
        if (condition) block() else this

    private fun String.replaceKtivWithQriIfNeeded(
        force: Boolean = false,
        isTikunKorim: Boolean = false,
    ): String =
        runIf(
            condition = settings.getBoolean(
                key = SharedPrefsConstants.ONLY_QRI_SWITCH,
                defaultValue = true,
            ) || force
        ) {
            replaceKtivWithQriNew(isTikunKorim = isTikunKorim)
        }

    private fun String.removeTeamimIfNeeded(force: Boolean = false): String =
        runIf(
            condition = !settings.getBoolean(
                key = Constants.PREF_SHOW_WITH_TEAMIM,
                defaultValue = true,
            ) || force
        ) {
            removeTeamimUseCase(pasuk = this)
        }

    private fun String.removeNikudIfNeeded(force: Boolean = false): String =
        runIf(
            condition = !settings.getBoolean(
                key = Constants.PREF_NIKUD_SWITCH,
                defaultValue = true,
            ) || force
        ) {
            removeNikudAndMakafUseCase(pasuk = this)
        }

    private fun String.removeGodNamesIfNeeded(force: Boolean = false): String =
        runIf(
            condition = settings.getBoolean(
                key = SharedPrefsConstants.HIDE_GOD_NAME,
                defaultValue = BooleanConstants.ENABLE_HIDE_GOD_NAME,
            ) || force
        ) {
            removeGodNamesUseCase(pasuk = this)
        }

    private fun String.removeVerseLetterIfNeeded(isTikunKorim: Boolean = false): String {
        return runIf(condition = settings.getBoolean(key = KEY_REMOVE_VERSE_LETTER, defaultValue = false)) {
            val eachVerseInNewLine = settings.getBoolean(key = KEY_EACH_VERSE_IN_NEW_LINE, defaultValue = false)
            val replacement = if (eachVerseInNewLine || isTikunKorim) "" else " "
            this
                .replaceRegex(regex = getRegexOfSingleOtPasukAleph(), replacement = "")
                .trim()
                .replaceOtiotPasuk(literalReplacement = replacement)
                .trim()
        }
    }

    private fun String.applyEachVerseInNewLineIfNeeded(): String {
        return runIf(
            condition = settings.getBoolean(
                key = KEY_EACH_VERSE_IN_NEW_LINE,
                defaultValue = BooleanConstants.ENABLE_EACH_VERSE_IN_NEW_LINE,
            )
        ) {
            replaceRegex(
                regex = "׃ ?",
                replacement = """
                     ׃
                
                     """.trimIndent()
            ).trim()
        }
    }
}
